import * as React from "react"
import { cn } from "../../../utils"
import { Label } from "./Label"
import { cva, VariantProps } from "class-variance-authority"
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline"

export interface InputProps
  extends
    React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  success?: boolean
  error?: boolean
  label?: string
  containerClassName?: string
  "data-testid"?: string
  showPasswordToggle?: boolean
  iconRight?: React.ReactNode
  tooltip?: string
  inputTextPrefix?: string
  onRightIconClick?: () => void
}

const inputVariants = cva(
  "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground flex h-9 w-full min-w-0 rounded px-3 py-1 text-base transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:ring-[1px] focus-visible:ring-primary-dark-blue focus-visible:border-transparent aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        grey: "bg-light-grey",
        white: "bg-white",
        transparent: "bg-transparent focus-visible:ring-[0px] p-0",
      },
    },
  },
)

function Input({
  className,
  variant = "white",
  type,
  error,
  success,
  label,
  containerClassName,
  required,
  showPasswordToggle,
  iconRight,
  tooltip,
  inputTextPrefix,
  onRightIconClick,
  ...props
}: InputProps) {
  const [showPassword, setShowPassword] = React.useState(false)

  const isPasswordField = type === "password"
  const inputType = isPasswordField && showPassword ? "text" : type

  const togglePasswordVisibility = React.useCallback(() => {
    setShowPassword((prev) => !prev)
  }, [])

  return (
    <div className={cn("flex flex-col gap-2 relative", containerClassName)}>
      {label && (
        <Label error={error} success={success} tooltip={tooltip}>
          {label}
          {required && <span className="text-red">*</span>}
        </Label>
      )}

      <div
        className={cn("relative", {
          "cursor-not-allowed": props.disabled,
        })}
      >
        {inputTextPrefix && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-dark-blue pointer-events-none">
            {inputTextPrefix}
          </div>
        )}
        <input
          type={inputType}
          data-slot="input"
          className={cn(inputVariants({ variant: variant }), className, {
            "ring-primary-green ring-[1px] text-primary-green": success,
            "ring-red ring-[1px] text-red": error,
            "opacity-50 cursor-not-allowed": props.disabled,
            "pr-10":
              (isPasswordField && (showPasswordToggle ?? true)) || !!iconRight,
            "pl-7": !!inputTextPrefix,
          })}
          {...props}
          data-testid={props["data-testid"]}
        />

        {iconRight && (
          <div
            onClick={onRightIconClick && onRightIconClick}
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-dark-blue focus:outline-none cursor-pointer"
          >
            {iconRight}
          </div>
        )}

        {isPasswordField && (showPasswordToggle ?? true) && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary-dark-blue focus:outline-none cursor-pointer"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? (
              <EyeSlashIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
        )}
      </div>
    </div>
  )
}

export default Input
